import { EventProvider } from "@/providers/EventProvider";
import { Switch, Route, useParams, useLocation } from "react-router-dom";
import { eventRoutes } from "@/routes/allRoutes";
import NotFound from "@/pages/Errors/NotFound";

const EventPage = () => {
  const location = useLocation();
  const params = useParams<{
    page?: string;
    eventId?: string;
    rest?: string;
    tenant?: string;
  }>();

  // Extract page and eventId from pathname to handle routes with additional segments
  // Handles both /gallery/123/guest-upload and /:domainPattern/gallery/123/guest-upload
  // e.g., /gallery/123/guest-upload -> page: "gallery", eventId: "123"
  // e.g., /photographer-name/gallery/123/guest-upload -> page: "gallery", eventId: "123"
  const pathSegments = location.pathname.split("/").filter(Boolean);

  // If domainPattern exists in params, skip the first segment (it's the domain pattern)
  // Otherwise, use the first segment as page
  const startIndex = params.tenant ? 1 : 0;
  const page = pathSegments[startIndex] || params.page;
  const eventId = pathSegments[startIndex + 1] || params.eventId;

  // Extract valid page names from eventRoutes (e.g., "gallery", "sponsoriq", "spotlight", "teams")
  const validPages = eventRoutes
    .map((route) => {
      // Extract page name from path like "/gallery/:eventId" -> "gallery"
      const match = route.path.match(/^\/([^/]+)/);
      return match ? match[1] : null;
    })
    .filter(Boolean) as string[];

  // Check if page param is valid
  const isPageValid = page && validPages.includes(page);

  // Check if eventId is a valid integer
  const isEventIdValid =
    eventId && /^\d+$/.test(eventId) && Number.isInteger(Number(eventId));

  // Show 404 if page is not valid or eventId is not a valid integer
  if (!isPageValid || !isEventIdValid) {
    return <NotFound />;
  }

  return (
    <EventProvider>
      <Switch>
        {eventRoutes.map((route, idx) => {
          // In React Router v5, routes match by prefix by default (no exact prop)
          // So "/gallery/:eventId" will match:
          // - "/gallery/123" ✓
          // - "/gallery/123/register" ✓
          // - "/gallery/123/guest-upload" ✓
          // - "/reelit/123/register" ✓
          return [
            <Route
              path={`/:domainPattern${route.path}`}
              key={`${idx}-domainPattern`}
              component={route.component}
            />,
            <Route
              path={route.path}
              key={`${idx}-eventRoute`}
              component={route.component}
            />,
          ];
        })}
      </Switch>
    </EventProvider>
  );
};

export default EventPage;
